
import {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react';
import Modal from '~/components/Modal';
import FullScreenModal from '~/components/FullScreenModal';
import AssistantInput from '~/components/assistant/AssistantInput.jsx';
import AssistantChatConversation from './AssistantChatConversation';

import { post, businessApiRoute } from '~/lib/Api';
import { useAssistantRecentConvosQuery } from '~/lib/Queries';
import { XMarkIcon } from '@heroicons/react/24/outline';
import SparklesIcon from '~/components/icons/SparklesIcon.jsx';
import RewindClockIcon from '~/components/icons/RewindClockIcon.jsx';
import emitter, { EmitterEventTypes } from '~/lib/emitter';
import { trackAmplitudeEvent } from '~/lib/AmplitudeTracking';

const AssistantChatbox = forwardRef(({ reload }, ref) => {
    const [showChat, setShowChat] = useState(false);
    // Mirrors showChat, but on close this flips to false immediately (kicking off the
    // Modal's leave transition) while showChat itself - which still gates whether the
    // whole modal tree is mounted at all - stays true until that transition finishes.
    // This keeps AssistantChatConversation's mount/unmount timing identical to before
    // (so the ref is always available the instant showChat becomes true, avoiding a
    // race with the auto-load-greeting effect) while still letting the close fade play.
    const [panelVisible, setPanelVisible] = useState(false);
    const closeTimeoutRef = useRef(null);
    const [isNewBusiness, setIsNewBusiness] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [showIdeas, setShowIdeas] = useState(false);
    const [isFullScreenVariant] = useState(() => Math.random() < 0.0); // Disable this test for now
    const conversationRef = useRef(null);
    const hasAutoLoadedRef = useRef(false);
    const hasTrackedFullScreenVariantRef = useRef(false);
    const messagesBeforeRecentRef = useRef(null);
    const { data: recentConvos } = useAssistantRecentConvosQuery();

    function openChatPanel(newIsNewBusiness) {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setShowChat(true);
        setPanelVisible(true);
        if (newIsNewBusiness !== undefined) {
            setIsNewBusiness(!!newIsNewBusiness);
        }
    }

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        openChat: isNewBusiness => openChatPanel(isNewBusiness),
        setMessages: (newMessages) => conversationRef.current?.setMessages(newMessages),
        getMessages: () => conversationRef.current?.getMessages?.(),
        submitPrompt: (thisPrompt) => conversationRef.current?.submitPrompt(thisPrompt),
        setPrompt: (text) => setPrompt(text),
    }));

    useEffect(() => {
        if (!showChat && isNewBusiness) {
            setIsNewBusiness(false);
        }
        if (!showChat) {
            hasAutoLoadedRef.current = false;
        }
    }, [showChat]);

    const defaultGreeting = [{ role: 'assistant', content: 'How can I help?' }];

    // Auto-load most recent chat if it was updated within the last 2 hours,
    // otherwise start a new chat with a greeting
    useEffect(() => {
        if (showChat && !isNewBusiness && !hasAutoLoadedRef.current && recentConvos) {
            hasAutoLoadedRef.current = true;
            const mostRecent = recentConvos?.[0];
            const updatedAt = mostRecent ? new Date(mostRecent.updated_at) : null;
            const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
            if (mostRecent && updatedAt > twoHoursAgo && mostRecent.results?.length > 0) {
                conversationRef.current?.setMessages(mostRecent.results);
            } else {
                conversationRef.current?.setMessages(defaultGreeting);
            }
        }
    }, [showChat, recentConvos, isNewBusiness]);

    // Listen for external requests to open assistant with a prompt
    useEffect(() => {
        let activeInterval = null;
        const handler = ({ prompt }) => {
            openChatPanel(false);
            // Wait for the chat to render and conversation ref to be available
            const maxAttempts = 20;
            let attempts = 0;
            const interval = activeInterval = setInterval(() => {
                if (attempts >= maxAttempts) {
                    clearInterval(interval);
                    activeInterval = null;
                    return;
                }
                if (conversationRef.current) {
                    clearInterval(interval);
                    activeInterval = null;
                    conversationRef.current.setMessages([{ role: 'assistant', content: 'How can I help?' }]);
                    setTimeout(() => {
                        conversationRef.current?.submitPrompt(prompt);
                    }, 100);
                } else {
                    attempts++;
                }
            }, 100);
        };
        emitter.on(EmitterEventTypes.OPEN_ASSISTANT_WITH_PROMPT, handler);
        return () => {
            emitter.off(EmitterEventTypes.OPEN_ASSISTANT_WITH_PROMPT, handler);
            if (activeInterval) clearInterval(activeInterval)
        }
    }, []);

    function closeChat() {
        post(businessApiRoute('/assistant/chat-closed'), { isNewBusiness });
        setPanelVisible(false);
        closeTimeoutRef.current = setTimeout(() => {
            setShowChat(false);
            closeTimeoutRef.current = null;
        }, 200);
    }

    function submit(e) {
        if (!prompt.trim()) return;
        e.preventDefault();
        conversationRef.current?.submitPrompt(prompt);
        setPrompt('');
    }

    const showFullScreenTakeover = isNewBusiness && isFullScreenVariant;
    const ChatModal = showFullScreenTakeover ? FullScreenModal : Modal;

    useEffect(() => {
        if (
            showChat &&
            isNewBusiness &&
            !hasTrackedFullScreenVariantRef.current
        ) {
            hasTrackedFullScreenVariantRef.current = true;
            trackAmplitudeEvent('Assistant Full Screen Takeover Test', {
                variation: showFullScreenTakeover
                    ? 'full_screen'
                    : 'standard',
                show_full_screen_takeover: showFullScreenTakeover,
            });
        }
    }, [showChat, isNewBusiness, showFullScreenTakeover]);

    return (
        <div className="w-full">
            {showChat && (
                <ChatModal
                    isOpen={panelVisible}
                    onClose={
                        isNewBusiness
                            ? () => null /*user must explicitly click the X*/
                            : closeChat
                    }
                    hideClose
                    //widthClass="sm:max-w-[80%] lg:max-w-[60%] xl:max-w-[50%]"
                    widthClass="max-w-[700px]"
                    panelClassName={`!bg-white !p-0`}
                >
                    {!showFullScreenTakeover && (
                        <div
                            className={
                                `flex [@media(min-width:530px)]:items-center justify-between gap-4 p-6 ${showIdeas ? '' : 'bg-gradient-to-r from-[#DAEBFF] via-[#FFFFFF] to-[#DAEBFF]'} border-b rounded-t-[22px]`
                            }
                        >
                            <div className="w-full">
                                {showIdeas ? (
                                    <div className="flex flex-wrap items-center gap-x-4">
                                        <div className="flex items-center justify-center bg-primary-200 border p-2 rounded-full">
                                            <RewindClockIcon className="text-primary-700 w-4 h-4" />
                                        </div>
                                        <div>
                                            <h2 className="!text-left">
                                                Recent Conversations
                                            </h2>
                                            <div className="text-sm text-gray-500">
                                                Revisit prior conversations
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2 items-center justify-between w-full">
                                        <div className="flex flex-wrap items-center gap-x-4">
                                            <div className="flex items-center justify-center bg-primary-700 p-2 rounded-full">
                                                <SparklesIcon className="text-white w-4 h-4" />
                                            </div>
                                            <div>
                                                <h2 className="!text-left">
                                                    Breely Assistant
                                                </h2>
                                                <div className="text-sm text-gray-500">
                                                    Personal Setup Expert · Replies in
                                                    seconds
                                                </div>
                                            </div>
                                        </div>
                                        {recentConvos?.length > 0 && (
                                            <button
                                                type="button"
                                                className="px-3 py-1.5 rounded-full border bg-white text-sm text-gray-600 hover:text-gray-700"
                                                onClick={() => {
                                                    // Save current messages before clearing
                                                    messagesBeforeRecentRef.current = conversationRef.current?.getMessages?.();
                                                    conversationRef.current?.setMessages([]);
                                                }}
                                            >
                                                Recent
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            {showIdeas ? (
                                <button
                                    //className="text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap"
                                    className="rounded-full p-1.5 border bg-gray-300 text-sm text-gray-600 hover:text-gray-700 whitespace-nowrap w-[32px] h-[32px]"
                                    onClick={() => {
                                        // Restore previous messages
                                        const prev = messagesBeforeRecentRef.current;
                                        conversationRef.current?.setMessages(prev || defaultGreeting);
                                        messagesBeforeRecentRef.current = null;
                                    }}
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            ) : !showFullScreenTakeover ? (
                                <button className="modal-header-btn w-[32px] h-[32px]" onClick={closeChat} aria-label="Close">
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            ) : null}
                        </div>
                    )}

                    <div className={showFullScreenTakeover ? 'max-w-2xl mx-auto' : ''}>
                        <AssistantChatConversation
                            ref={conversationRef}
                            showChat={showChat}
                            isNewBusiness={isNewBusiness}
                            setShowChat={setShowChat}
                            onShowIdeasChange={setShowIdeas}
                            hideIdeasAndInput={true}
                            source="AssistantChatbox"
                            className={showFullScreenTakeover ? 'pt-6' : 'p-6'}
                        />
                        {showFullScreenTakeover && (
                            <button
                                type="button"
                                className="text-xs text-gray-500 ml-4 -mt-4 block"
                                onClick={() => {
                                    trackAmplitudeEvent(
                                        'Assistant Explore On Your Own Clicked',
                                        {
                                            variation: 'full_screen',
                                            show_full_screen_takeover: true,
                                        },
                                    );
                                    closeChat();
                                }}
                            >
                                The assistant can get you setup in minutes, but you can also <span className="underline">explore on your own</span>.
                            </button>
                        )}
                    </div>

                </ChatModal>
            )}
            <div className={`${showChat ? 'h-[42px]' : ''}`}>
                <div className="bg-white relative rounded-lg w-full">
                    {!showChat && (
                        <AssistantInput
                            prompt={prompt}
                            setPrompt={setPrompt}
                            isRunning={false}
                            submit={submit}
                            onFocus={() => openChatPanel()}
                            showChat={showChat}
                            className="px-4"
                        />
                    )}
                </div>
            </div>
        </div>
    );
});

export default AssistantChatbox;
